// Google Analytics event tracking hook
// Uses gtag for tracking user interactions

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

type EventParams = {
  category?: string;
  label?: string;
  value?: number;
  [key: string]: unknown;
};

export const trackEvent = (
  action: string,
  params?: EventParams
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: params?.category || 'engagement',
      event_label: params?.label,
      value: params?.value,
      ...params,
    });
  }
};

// Pre-defined tracking functions for common events
export const analytics = {
  // Tool events
  toolOpened: (toolId: string, toolName: string) => {
    trackEvent('tool_opened', {
      category: 'tools',
      label: toolName,
      tool_id: toolId,
    });
  },
  
  toolUsed: (toolId: string, toolName: string, action?: string) => {
    trackEvent('tool_used', {
      category: 'tools',
      label: `${toolName}${action ? ` - ${action}` : ''}`,
      tool_id: toolId,
    });
  },
  
  // Lead capture events
  leadFormOpened: (source: string) => {
    trackEvent('lead_form_opened', {
      category: 'lead_capture',
      label: source,
    });
  },
  
  leadFormSubmitted: (source: string) => {
    trackEvent('lead_form_submitted', {
      category: 'lead_capture',
      label: source,
    });
  },
  
  // CTA events
  ctaClicked: (ctaName: string, destination?: string) => {
    trackEvent('cta_clicked', {
      category: 'cta',
      label: ctaName,
      destination,
    });
  },
  
  // External link events
  externalLinkClicked: (url: string, context?: string) => {
    trackEvent('external_link_clicked', {
      category: 'outbound',
      label: url,
      context,
    });
  },
  
  // Content events
  insightViewed: (insightSlug: string, insightTitle: string) => {
    trackEvent('insight_viewed', {
      category: 'content',
      label: insightTitle,
      insight_slug: insightSlug,
    });
  },
  
  // Data export events
  dataExported: (format: string, context: string) => {
    trackEvent('data_exported', {
      category: 'engagement',
      label: `${context} - ${format}`,
    });
  },
  
  // Issue reporting
  issueReported: (toolName: string, issueType: string) => {
    trackEvent('issue_reported', {
      category: 'feedback',
      label: `${toolName} - ${issueType}`,
    });
  },
};

export const useAnalytics = () => {
  return analytics;
};
